import { useState, useEffect, useRef } from "react";

/**
 * DecimalInput — drop-in replacement for `<input type="number">` that does NOT
 * eat intermediate zeros. Lets the user type 0.07 normally.
 *
 * Internally tracks the raw typed string so partial values ("0", "0.", "0.0")
 * remain visible. The parent still receives a Number via onChange.
 *
 * Props mirror a normal input: value, onChange(number), className,
 * placeholder, disabled, data-testid, etc. The `step` prop is accepted for
 * API parity but unused (we use text input).
 */
export default function DecimalInput({
  value,
  onChange,
  className,
  placeholder = "0",
  step,
  disabled = false,
  allowNegative = false,
  ...rest
}) {
  const toNum = (v) => {
    if (v === null || v === undefined || v === "") return 0;
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  };

  const displayFromValue = (v) => {
    const n = toNum(v);
    return n === 0 ? "" : String(v);
  };

  const [text, setText] = useState(() => displayFromValue(value));
  const focusedRef = useRef(false);

  // Sync external value changes (auto-calc, reset, hydrate from server) but
  // ONLY when the user is not actively typing — otherwise we'd wipe their input.
  useEffect(() => {
    if (focusedRef.current) return;
    const incoming = toNum(value);
    const current = toNum(text);
    if (incoming !== current) setText(displayFromValue(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value;
    const re = allowNegative ? /^-?[0-9]*\.?[0-9]*$/ : /^[0-9]*\.?[0-9]*$/;
    if (raw === "" || re.test(raw)) {
      setText(raw);
      const num =
        raw === "" || raw === "." || raw === "-" || raw === "-."
          ? 0
          : parseFloat(raw);
      onChange(isNaN(num) ? 0 : num);
    }
  };

  const handleBlur = () => {
    focusedRef.current = false;
    // Normalize the user's OWN typed entry on blur (e.g. "0.700" → "0.7", "500" → "500").
    // We deliberately do NOT re-derive from the `value` prop here: if the parent's state
    // hasn't caught up for any reason, re-reading it would silently yank the field back to
    // the old number (e.g. an MOQ typed as 500 snapping back to 2000). Trust what they typed —
    // onChange has already pushed the parsed number up to the parent during typing.
    const typed = (text || "").trim();
    if (typed === "" || typed === "." || typed === "-" || typed === "-.") {
      setText("");
      return;
    }
    const n = parseFloat(typed);
    setText(isNaN(n) || n === 0 ? "" : String(n));
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={text}
      onChange={handleChange}
      onFocus={() => { focusedRef.current = true; }}
      onBlur={handleBlur}
      className={className}
      placeholder={placeholder}
      disabled={disabled}
      {...rest}
    />
  );
}
